import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarcodeDetector, prepareZXingModule } from 'barcode-detector/ponyfill';
import apiClient from '../hooks/apiClient';

// WASM-модуль распознавания отдаём со своего сервера (кладётся в build при сборке),
// а не с CDN, и начинаем грузить сразу при открытии страницы
prepareZXingModule({
  overrides: {
    locateFile: (path, prefix) =>
      path.endsWith('.wasm') ? `${process.env.PUBLIC_URL}/zxing_reader.wasm` : prefix + path,
  },
  fireImmediately: true,
}).catch(() => {});

const SCAN_INTERVAL_MS = 80;
// Детектору отдаём область чуть больше видимой рамки: код у края рамки тоже читается
const CROP_MARGIN = 1.3;
// Стартовый зум: у основной камеры многих телефонов (особенно iPhone Pro) минимальная
// дистанция фокуса 15–20 см. С зумом телефон держат дальше, и мелкий код остаётся резким
const DEFAULT_ZOOM = 2;
const ZOOM_PRESETS = [1, 2, 3];
const CAMERA_STORAGE_KEY = 'scanqr_camera_id';
const SUCCESS_CLOSE_DELAY_MS = 1200;

const tg = window.Telegram?.WebApp;

const haptic = (type) => {
  try {
    tg?.HapticFeedback?.notificationOccurred(type);
  } catch (_) {}
};

const storage = {
  get() {
    try {
      return localStorage.getItem(CAMERA_STORAGE_KEY);
    } catch (_) {
      return null;
    }
  },
  set(value) {
    try {
      localStorage.setItem(CAMERA_STORAGE_KEY, value);
    } catch (_) {}
  },
};

const openCamera = (deviceId) =>
  navigator.mediaDevices.getUserMedia({
    audio: false,
    video: {
      ...(deviceId ? { deviceId: { exact: deviceId } } : { facingMode: { ideal: 'environment' } }),
      width: { ideal: 1920 },
      height: { ideal: 1080 },
    },
  });

const stopStream = (stream) => stream?.getTracks().forEach((t) => t.stop());

// Android (Chrome) сообщает режимы фокуса камеры. Если continuous нет — это, как правило,
// широкоугольная камера с фиксированным фокусом. iOS режимы не сообщает, но фокусирует сам
const hasAutofocus = (track) => {
  const modes = track.getCapabilities?.().focusMode;
  return !modes || modes.includes('continuous');
};

// На Android facingMode: environment иногда отдаёт камеру без автофокуса.
// Тогда перебираем остальные задние камеры и берём первую с continuous-фокусом
const openBestCamera = async () => {
  const savedId = storage.get();
  let stream = null;
  if (savedId) {
    stream = await openCamera(savedId).catch(() => null);
  }
  if (!stream) {
    stream = await openCamera();
  }

  let track = stream.getVideoTracks()[0];
  if (hasAutofocus(track)) return stream;

  const currentId = track.getSettings().deviceId;
  const devices = await navigator.mediaDevices.enumerateDevices();
  const candidates = devices
    .filter((d) => d.kind === 'videoinput' && d.deviceId !== currentId)
    .filter((d) => /back|rear|environment|задн/i.test(d.label))
    .sort((a, b) => a.label.localeCompare(b.label));

  for (const device of candidates) {
    stopStream(stream);
    stream = await openCamera(device.deviceId).catch(() => null);
    if (!stream) continue;
    track = stream.getVideoTracks()[0];
    if (hasAutofocus(track)) {
      storage.set(device.deviceId);
      return stream;
    }
  }

  // Камеры с автофокусом не нашлось — возвращаемся к камере по умолчанию
  stopStream(stream);
  return openCamera();
};

const applyAdvanced = (track, constraints) =>
  track.applyConstraints({ advanced: [constraints] }).catch(() => {});

const getFrameSize = () =>
  Math.round(Math.min(window.innerWidth * 0.72, window.innerHeight * 0.42, 320));

const ScanQR = ({ onScanSuccess }) => {
  // idle → scanning | manual → sending → success | pending | error
  const [phase, setPhase] = useState('idle');
  const navigate = useNavigate();
  const [message, setMessage] = useState('');
  const [result, setResult] = useState(null);
  const [manual, setManual] = useState({ gtin: '', serial: '' });
  const photoRef = useRef(null);
  const [hint, setHint] = useState('');
  const [torchSupported, setTorchSupported] = useState(false);
  const [torchOn, setTorchOn] = useState(false);
  const [zoomRange, setZoomRange] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [focusPoint, setFocusPoint] = useState(null);
  const [frameSize, setFrameSize] = useState(getFrameSize);
  const [restartKey, setRestartKey] = useState(0);

  const videoRef = useRef(null);
  const trackRef = useRef(null);
  const cbRef = useRef(null);
  const frameSizeRef = useRef(frameSize);
  frameSizeRef.current = frameSize;

  useEffect(() => {
    tg?.ready();
    tg?.expand();
    const onResize = () => setFrameSize(getFrameSize());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Отправка кода на бэкенд: /v1/check_code проверяет подпись Telegram,
  // формат кода маркировки, дубликаты и дневной лимит
  // С камеры — поверх видео (sending), из фото и ручного ввода — отдельным экраном (checking)
  const submitCode = async (code, fromCamera = true) => {
    setPhase(fromCamera ? 'sending' : 'checking');
    try {
      const { data } = await apiClient.post('/v1/check_code', { code });
      haptic('success');
      setResult(data);
      // Пачки блока на отслеживании — баллы придут по мере продажи, бот пришлёт уведомления
      if (data?.status === 'pending') {
        setPhase('pending');
        return;
      }
      setPhase('success');
      setTimeout(() => tg?.close(), SUCCESS_CLOSE_DELAY_MS);
    } catch (e) {
      haptic('error');
      const detail = e.response?.data?.detail;
      setMessage(typeof detail === 'string' ? detail : 'Не удалось проверить код. Попробуйте ещё раз');
      setPhase('error');
    }
  };

  // Колбэк через ref: цикл сканирования не перезапускается при новом рендере
  cbRef.current = onScanSuccess || submitCode;

  const scanning = phase === 'scanning';

  useEffect(() => {
    if (!scanning) return;

    let stopped = false;
    let stream = null;
    let timer = null;

    const detector = new BarcodeDetector({ formats: ['data_matrix', 'qr_code'] });
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d', { willReadFrequently: true });

    (async () => {
      try {
        stream = await openBestCamera();
        if (stopped) {
          stopStream(stream);
          return;
        }

        const track = stream.getVideoTracks()[0];
        trackRef.current = track;
        // iOS гасит камеру, когда Mini App уходит в фон, — после возврата открываем заново
        track.addEventListener('ended', () => {
          if (!stopped) setRestartKey((k) => k + 1);
        });

        const caps = track.getCapabilities?.() || {};
        if (caps.focusMode?.includes('continuous')) {
          await applyAdvanced(track, { focusMode: 'continuous' });
        }
        setTorchSupported(!!caps.torch);
        if (caps.zoom && caps.zoom.max > caps.zoom.min) {
          const initialZoom = Math.min(Math.max(DEFAULT_ZOOM, caps.zoom.min), caps.zoom.max);
          setZoomRange({ min: caps.zoom.min, max: caps.zoom.max });
          setZoom(initialZoom);
          await applyAdvanced(track, { zoom: initialZoom });
        } else {
          setZoomRange(null);
        }

        const video = videoRef.current;
        video.srcObject = stream;
        await video.play();

        const tick = async () => {
          if (stopped) return;
          try {
            const vw = video.videoWidth;
            const vh = video.videoHeight;
            const ew = video.clientWidth;
            const eh = video.clientHeight;
            if (video.readyState >= 2 && vw && vh && ew && eh) {
              // Видео растянуто на экран по object-fit: cover — переводим рамку
              // из экранных пикселей в пиксели кадра и режем только её
              const scale = Math.max(ew / vw, eh / vh);
              const side = Math.floor(
                Math.min(vw, vh, (frameSizeRef.current * CROP_MARGIN) / scale)
              );
              if (canvas.width !== side) {
                canvas.width = side;
                canvas.height = side;
              }
              ctx.drawImage(video, (vw - side) / 2, (vh - side) / 2, side, side, 0, 0, side, side);

              const codes = await detector.detect(canvas);
              const code = codes.find((c) => c.rawValue)?.rawValue;
              if (code && !stopped) {
                // На упаковке бывает и QR со ссылкой — его пропускаем и сканируем дальше
                if (/^https?:\/\//i.test(code)) {
                  setHint('Это QR со ссылкой. Наведите рамку на квадратный код на наклейке блока');
                } else {
                  stopped = true;
                  cbRef.current(code);
                  return;
                }
              }
            }
          } catch (_) {
            /* пропускаем кадр */
          }
          timer = setTimeout(tick, SCAN_INTERVAL_MS);
        };
        tick();
      } catch (e) {
        console.error(e);
        haptic('error');
        setMessage(
          e?.name === 'NotAllowedError'
            ? 'Нет доступа к камере. Разрешите доступ к камере для Telegram в настройках телефона'
            : 'Не удалось запустить камеру. Закройте другие приложения, которые её используют, и попробуйте снова'
        );
        setPhase('error');
      }
    })();

    return () => {
      stopped = true;
      clearTimeout(timer);
      stopStream(stream);
      trackRef.current = null;
      setTorchOn(false);
      setHint('');
      setFocusPoint(null);
    };
  }, [scanning, restartKey]);

  const start = () => {
    setMessage('');
    setPhase('scanning');
  };

  const close = () => {
    setPhase('idle');
  };

  // Код с наклейки блока: «(01) 04006396046147» и «(21) lJ4MQy5» — сервер ждёт 01 GTIN 21 серийный
  const manualGtin = manual.gtin.replace(/\D/g, '');
  const manualSerial = manual.serial.trim();
  const manualOk = (manualGtin.length === 13 || manualGtin.length === 14) && /^[!-~]{7}$/.test(manualSerial);
  const submitManual = (e) => {
    e.preventDefault();
    if (!manualOk) return;
    submitCode(`01${manualGtin.padStart(14, '0')}21${manualSerial}`, false);
  };

  // Скан по фото: тот же распознаватель, что у камеры, на выбранном снимке
  const scanPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setPhase('checking');
    try {
      const bitmap = await createImageBitmap(file);
      const detector = new BarcodeDetector({ formats: ['data_matrix', 'qr_code'] });
      const codes = await detector.detect(bitmap);
      const code = codes.map((c) => c.rawValue).find((v) => v && !/^https?:\/\//i.test(v));
      if (code) {
        submitCode(code, false);
        return;
      }
      setMessage('На фото не найден код блока. Сфотографируйте наклейку ближе и ровнее или введите код вручную');
    } catch (_) {
      setMessage('Не удалось прочитать фото. Попробуйте другой снимок или введите код вручную');
    }
    haptic('error');
    setPhase('error');
  };

  const toggleTorch = async () => {
    const track = trackRef.current;
    if (!track) return;
    try {
      await track.applyConstraints({ advanced: [{ torch: !torchOn }] });
      setTorchOn((v) => !v);
    } catch (_) {}
  };

  const changeZoom = (value) => {
    const track = trackRef.current;
    if (!track || !zoomRange) return;
    const next = Math.min(Math.max(value, zoomRange.min), zoomRange.max);
    setZoom(next);
    applyAdvanced(track, { zoom: next });
  };

  // Фокус по тапу. Работает там, где камера поддерживает точку фокуса (Android Chrome);
  // на iOS автофокус непрерывный и тап просто показывает кольцо
  const focusAt = useCallback(async (e) => {
    const track = trackRef.current;
    const video = videoRef.current;
    if (!track || !video?.videoWidth) return;

    const rect = video.getBoundingClientRect();
    const px = e.clientX - rect.left;
    const py = e.clientY - rect.top;
    setFocusPoint({ x: px, y: py, id: Date.now() });

    const vw = video.videoWidth;
    const vh = video.videoHeight;
    const scale = Math.max(rect.width / vw, rect.height / vh);
    const x = Math.min(Math.max(((px - rect.width / 2) / scale + vw / 2) / vw, 0), 1);
    const y = Math.min(Math.max(((py - rect.height / 2) / scale + vh / 2) / vh, 0), 1);

    const caps = track.getCapabilities?.() || {};
    if (!caps.focusMode) return;
    const mode = caps.focusMode.includes('single-shot') ? 'single-shot' : 'continuous';
    await applyAdvanced(track, { focusMode: mode, pointsOfInterest: [{ x, y }] });
    if (mode === 'single-shot' && caps.focusMode.includes('continuous')) {
      setTimeout(() => {
        if (trackRef.current === track) applyAdvanced(track, { focusMode: 'continuous' });
      }, 2000);
    }
  }, []);

  const zoomPresets = zoomRange
    ? ZOOM_PRESETS.filter((z) => z >= zoomRange.min && z <= zoomRange.max)
    : [];

  return (
    <div className="sq-root">
      <style>{styles}</style>

      {phase === 'idle' && (
        <div className="sq-screen">
          <div className="sq-icon">
            <div className="sq-icon-code" />
          </div>
          <h1 className="sq-title">Сканирование блока</h1>
          <p className="sq-text">
            Наведите камеру на квадратный код на наклейке блока (рядом с «Made in…» и номером). Он начнёт считываться сам.
            Коды пачек не принимаются
          </p>
          <button className="sq-button" onClick={start}>
            Включить камеру
          </button>
          <div className="sq-alt">
            <button className="sq-button sq-button-secondary" onClick={() => photoRef.current?.click()}>
              🖼 По фото
            </button>
            <button className="sq-button sq-button-secondary" onClick={() => setPhase('manual')}>
              ⌨️ Ввести код
            </button>
          </div>
          <input ref={photoRef} type="file" accept="image/*" hidden onChange={scanPhoto} />
          <button className="sq-link" onClick={() => navigate('/blocks')}>
            📦 Мои блоки
          </button>
          <button className="sq-link sq-link-tight" onClick={() => navigate('/balance')}>
            💰 Баланс и история
          </button>
        </div>
      )}

      {phase === 'manual' && (
        <form className="sq-screen" onSubmit={submitManual}>
          <h1 className="sq-title">Код с наклейки блока</h1>
          <p className="sq-text sq-text-tight">
            На наклейке рядом с квадратным кодом напечатаны две строки: <b>(01)</b> и 14 цифр, <b>(21)</b> и 7 символов
          </p>
          <label className="sq-field">(01) — штрихкод, цифры
            <input inputMode="numeric" autoComplete="off" placeholder="04006396046147" value={manual.gtin}
                   onChange={(e) => setManual({ ...manual, gtin: e.target.value })} />
          </label>
          <label className="sq-field">(21) — серийный номер, 7 символов
            <input autoComplete="off" autoCapitalize="off" spellCheck={false} placeholder="lJ4MQy5" maxLength={7}
                   value={manual.serial} onChange={(e) => setManual({ ...manual, serial: e.target.value })} />
          </label>
          <p className="sq-text sq-text-tight">Различайте похожие символы: l (строчная L), I (заглавная i) и 1; O и 0</p>
          <button className="sq-button" type="submit" disabled={!manualOk}>Проверить блок</button>
          <button className="sq-link" type="button" onClick={close}>Назад</button>
        </form>
      )}

      {phase === 'checking' && (
        <div className="sq-screen">
          <div className="sq-spinner" />
          <p className="sq-text">Проверяем блок…</p>
        </div>
      )}

      {(scanning || phase === 'sending') && (
        <div className="sq-camera">
          <video
            ref={videoRef}
            className="sq-video"
            playsInline
            muted
            autoPlay
            onClick={focusAt}
          />

          <div className="sq-frame" style={{ width: frameSize, height: frameSize }}>
            <span className="sq-corner sq-tl" />
            <span className="sq-corner sq-tr" />
            <span className="sq-corner sq-bl" />
            <span className="sq-corner sq-br" />
            {scanning && <span className="sq-line" />}
          </div>

          {focusPoint && (
            <span
              key={focusPoint.id}
              className="sq-focus"
              style={{ left: focusPoint.x, top: focusPoint.y }}
            />
          )}

          <div className="sq-top">
            <button className="sq-round" onClick={close} aria-label="Закрыть">
              ✕
            </button>
            {torchSupported && (
              <button
                className={`sq-round${torchOn ? ' sq-round-on' : ''}`}
                onClick={toggleTorch}
                aria-label="Фонарик"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M7 2h10v4l-3 4v12h-4V10L7 6V2zm2 2v1.4l3 4 3-4V4H9z" />
                </svg>
              </button>
            )}
          </div>

          <div className="sq-bottom">
            <p className={`sq-hint${hint ? ' sq-hint-warn' : ''}`}>
              {hint || 'Держите телефон в 10–20 см от наклейки блока. Нажмите на экран, чтобы навести фокус'}
            </p>
            {zoomPresets.length > 1 && (
              <div className="sq-zoom">
                {zoomPresets.map((z) => (
                  <button
                    key={z}
                    className={`sq-zoom-btn${Math.abs(zoom - z) < 0.05 ? ' sq-zoom-on' : ''}`}
                    onClick={() => changeZoom(z)}
                  >
                    {z}×
                  </button>
                ))}
              </div>
            )}
          </div>

          {phase === 'sending' && (
            <div className="sq-overlay">
              <div className="sq-spinner" />
              <p>Проверяем код…</p>
            </div>
          )}
        </div>
      )}

      {phase === 'success' && (
        <div className="sq-screen">
          <div className="sq-result sq-result-ok">✓</div>
          <h1 className="sq-title">Код принят</h1>
          <p className="sq-text">Баллы начислены, бот пришлёт уведомление</p>
          {!tg?.initData && (
            <button className="sq-button" onClick={start}>
              Сканировать ещё
            </button>
          )}
        </div>
      )}

      {phase === 'pending' && (
        <div className="sq-screen">
          <div className="sq-result sq-result-wait">⏳</div>
          <h1 className="sq-title">Блок принят</h1>
          <p className="sq-text">
            {result?.composition_pending
              ? 'ИС МПТ сейчас не отвечает — список пачек получим позже, бот пришлёт уведомление.'
              : `${result?.packs_tracked ?? ''} пачек на отслеживании до ${result?.tracked_until ?? ''}. `
                + 'Продайте все — баллы за каждую пачку придут автоматически.'}
            {result?.packs_presold > 0 && ` Ещё ${result.packs_presold} выведено из оборота до скана — за них баллов нет.`}
          </p>
          <button className="sq-button" onClick={start}>
            Сканировать ещё
          </button>
          <button className="sq-link" onClick={() => navigate('/blocks')}>
            📦 Мои блоки
          </button>
        </div>
      )}

      {phase === 'error' && (
        <div className="sq-screen">
          <div className="sq-result sq-result-err">!</div>
          <h1 className="sq-title">Не получилось</h1>
          <p className="sq-text">{message}</p>
          <button className="sq-button" onClick={start}>
            Сканировать снова
          </button>
        </div>
      )}
    </div>
  );
};

const styles = `
  .sq-root {
    position: fixed;
    inset: 0;
    z-index: 100;
    display: flex;
    flex-direction: column;
    background: var(--tg-theme-bg-color, #17212b);
    color: var(--tg-theme-text-color, #f5f5f5);
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    -webkit-tap-highlight-color: transparent;
  }
  .sq-screen {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 24px 24px calc(24px + env(safe-area-inset-bottom));
    text-align: center;
  }
  .sq-icon {
    width: 96px;
    height: 96px;
    border-radius: 28px;
    background: var(--tg-theme-secondary-bg-color, #232e3c);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 28px;
  }
  .sq-icon-code {
    width: 40px;
    height: 40px;
    border-radius: 6px;
    background:
      linear-gradient(90deg, #10b981 3px, transparent 3px) 0 0 / 8px 8px,
      linear-gradient(#10b981 3px, transparent 3px) 0 0 / 8px 8px;
    border-left: 3px solid #10b981;
    border-bottom: 3px solid #10b981;
    opacity: 0.9;
  }
  .sq-title {
    font-size: 22px;
    font-weight: 700;
    margin: 0 0 10px;
  }
  .sq-text {
    font-size: 15px;
    line-height: 1.45;
    color: var(--tg-theme-hint-color, #8b98a5);
    max-width: 320px;
    margin: 0 0 32px;
  }
  .sq-button {
    width: 100%;
    max-width: 360px;
    padding: 16px;
    border: none;
    border-radius: 14px;
    background: #10b981;
    color: #fff;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
  }
  .sq-button:active { opacity: 0.85; }
  .sq-button:disabled { opacity: 0.5; }
  .sq-button-secondary { background: var(--tg-theme-secondary-bg-color, #232e3c); color: var(--tg-theme-text-color, #f5f5f5); }
  .sq-alt { display: flex; gap: 10px; width: 100%; max-width: 360px; margin-top: 10px; }
  .sq-alt .sq-button { padding: 13px 8px; font-size: 15px; }
  .sq-link-tight { margin-top: 0; }
  .sq-text-tight { margin-bottom: 16px; font-size: 14px; }
  .sq-field { display: flex; flex-direction: column; gap: 6px; width: 100%; max-width: 360px; margin-bottom: 14px;
              text-align: left; font-size: 13px; color: var(--tg-theme-hint-color, #8b98a5); }
  .sq-field input { padding: 14px; border-radius: 12px; border: 1px solid rgba(255, 255, 255, 0.15);
                    background: var(--tg-theme-secondary-bg-color, #232e3c); color: var(--tg-theme-text-color, #f5f5f5);
                    font-size: 18px; letter-spacing: 0.04em; font-family: ui-monospace, Menlo, monospace; }
  .sq-link {
    margin-top: 14px;
    padding: 12px;
    border: none;
    background: transparent;
    color: var(--tg-theme-link-color, #6ab3f3);
    font-size: 15px;
    cursor: pointer;
  }
  .sq-result-wait { background: #f59e0b; }

  .sq-camera {
    position: relative;
    flex: 1;
    overflow: hidden;
    background: #000;
  }
  .sq-video {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  .sq-frame {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    border-radius: 20px;
    box-shadow: 0 0 0 100vmax rgba(0, 0, 0, 0.55);
    pointer-events: none;
  }
  .sq-corner {
    position: absolute;
    width: 30px;
    height: 30px;
    border: 0 solid #10b981;
  }
  .sq-tl { top: 0; left: 0; border-top-width: 4px; border-left-width: 4px; border-top-left-radius: 20px; }
  .sq-tr { top: 0; right: 0; border-top-width: 4px; border-right-width: 4px; border-top-right-radius: 20px; }
  .sq-bl { bottom: 0; left: 0; border-bottom-width: 4px; border-left-width: 4px; border-bottom-left-radius: 20px; }
  .sq-br { bottom: 0; right: 0; border-bottom-width: 4px; border-right-width: 4px; border-bottom-right-radius: 20px; }
  .sq-line {
    position: absolute;
    left: 8%;
    right: 8%;
    height: 2px;
    background: #10b981;
    box-shadow: 0 0 12px #10b981;
    animation: sq-scan 2s ease-in-out infinite;
  }
  @keyframes sq-scan {
    0%, 100% { top: 8%; opacity: 0.7; }
    50% { top: 92%; opacity: 1; }
  }
  .sq-focus {
    position: absolute;
    width: 64px;
    height: 64px;
    margin: -32px 0 0 -32px;
    border: 2px solid #ffd60a;
    border-radius: 50%;
    pointer-events: none;
    animation: sq-focus 0.9s ease-out forwards;
  }
  @keyframes sq-focus {
    0% { transform: scale(1.4); opacity: 0; }
    30% { transform: scale(1); opacity: 1; }
    100% { transform: scale(1); opacity: 0; }
  }
  .sq-top {
    position: absolute;
    top: calc(12px + env(safe-area-inset-top));
    left: 12px;
    right: 12px;
    display: flex;
    justify-content: space-between;
  }
  .sq-round {
    width: 44px;
    height: 44px;
    border: none;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.5);
    color: #fff;
    font-size: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
  }
  .sq-round-on { background: #ffd60a; color: #000; }
  .sq-bottom {
    position: absolute;
    left: 0;
    right: 0;
    bottom: calc(20px + env(safe-area-inset-bottom));
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 14px;
    padding: 0 20px;
  }
  .sq-hint {
    margin: 0;
    max-width: 320px;
    padding: 10px 14px;
    border-radius: 12px;
    background: rgba(0, 0, 0, 0.5);
    color: #e4e4e7;
    font-size: 13px;
    line-height: 1.4;
    text-align: center;
  }
  .sq-hint-warn { background: rgba(245, 158, 11, 0.9); color: #111; }
  .sq-zoom {
    display: flex;
    gap: 6px;
    padding: 4px;
    border-radius: 999px;
    background: rgba(0, 0, 0, 0.5);
  }
  .sq-zoom-btn {
    min-width: 44px;
    height: 36px;
    border: none;
    border-radius: 999px;
    background: transparent;
    color: #fff;
    font-size: 14px;
    font-weight: 600;
  }
  .sq-zoom-on { background: rgba(255, 255, 255, 0.9); color: #000; }
  .sq-overlay {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 14px;
    background: rgba(0, 0, 0, 0.65);
    color: #fff;
    font-size: 15px;
  }
  .sq-spinner {
    width: 42px;
    height: 42px;
    border: 4px solid rgba(255, 255, 255, 0.25);
    border-top-color: #10b981;
    border-radius: 50%;
    animation: sq-spin 0.8s linear infinite;
  }
  @keyframes sq-spin { to { transform: rotate(360deg); } }
  .sq-result {
    width: 88px;
    height: 88px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 44px;
    font-weight: 700;
    color: #fff;
    margin-bottom: 24px;
    animation: sq-pop 0.35s ease-out;
  }
  .sq-result-ok { background: #10b981; }
  .sq-result-err { background: #ef4444; }
  @keyframes sq-pop {
    0% { transform: scale(0.5); opacity: 0; }
    100% { transform: scale(1); opacity: 1; }
  }
`;

export default ScanQR;
